import React, { useMemo } from 'react'
import { useMatch } from 'react-router-dom'
import { useFetchUsersAccountLinks } from '../api/use-fetch-users-account-links'
import { getHagerBridgePermissions } from '../hooks/get-hager-bridge-permissions'
import { HagerBridgePermissionContext, HagerBridgePermissionContextValue } from './HagerBridgePermissionContext'
import { UserData } from 'src/State/User/UserModel'

interface HagerBridgePermissionProviderProps {
  children: React.ReactNode
  userData: UserData | undefined
}

/**
 * This provider gives the information on if a user has only read or also write rights for a given installation per feature.
 *
 * This is especially relevant for users who are invited to an installation (user, installer, or partner).
 * Those users are linked to the installation via the accountLinks, which includes the information about
 * their access rights.
 *
 * For higher user levels, such as service, this linkage is irrelevant. Instead, those levels can generally access all installations with writing rights.
 *
 * Read/Write access rights are configured not on the side of E3/DC e-mobility service, but HagerBridge service developed by P3. Its permission is configured
 * separately and cannot be reused for any other features apart from e-mobility.
 */
export const HagerBridgePermissionProvider: React.FC<HagerBridgePermissionProviderProps> = ({ children, userData }) => {
  const match = useMatch('/e-mobility/:installationId/*')
  const installationId = match?.params.installationId
  const { accountLinks } = useFetchUsersAccountLinks(Number(installationId))
  const permissions = getHagerBridgePermissions(accountLinks, userData)
  const contextValue = useMemo<HagerBridgePermissionContextValue>(
    () => ({
      permissions,
    }),
    [permissions]
  )

  return <HagerBridgePermissionContext.Provider value={contextValue}>{children}</HagerBridgePermissionContext.Provider>
}
